import { useState } from "react";
import { Box, Typography, TextField, Chip } from "@mui/material";
import { useAppTheme } from "@/hooks/useAppTheme";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { resolveCodeChipPalette } from "@/components/common/fields/codeChipColors";
import { AutocompleteCodeInput } from "@/components/common/fields/AutocompleteCodeInput";
import type { RefCodeType } from "@/hooks/useRefCodeSearch";

/** Color keys for which the ref-code typeahead (AutocompleteCodeInput) should render. */
const REF_CODE_COLORS: ReadonlySet<string> = new Set(["icd", "cpt", "ndc"]);

/** Default number of chips visible before collapsing in read mode */
const DEFAULT_COLLAPSE_THRESHOLD = 10;

interface EditableCodeChipSectionProps {
  /** Display label for the section header */
  label: string;
  /** Current list of code values */
  codes: string[];
  /** Color key from CODE_CHIP_COLORS */
  color: string;
  /** Whether editing is allowed (user has edit permission) */
  canEdit: boolean;
  /** Called with the full updated codes array when user adds or removes a code */
  onUpdate: (codes: string[]) => void;
  /** Placeholder text for the add input */
  placeholder?: string;
  /** Max length for input (enforces DB column constraints like CHAR(2), CHAR(5)) */
  maxLength?: number;
  /** Number of chips to show before collapsing (default: 10). Set to 0 to disable. */
  collapseThreshold?: number;
  /** Start in editing mode (default: false). Useful in create forms. */
  defaultEditing?: boolean;
}

/**
 * Renders a labeled section of code/value chips with optional inline editing.
 * When `canEdit` is true, shows an Edit/Done toggle. In edit mode, chips are
 * deletable and a text input allows adding new codes.
 *
 * Extracted from EndorsementDetailPanel's inline ICD/CPT editing pattern.
 */
export const EditableCodeChipSection = ({
  label,
  codes,
  color,
  canEdit,
  onUpdate,
  placeholder = "Add code...",
  maxLength,
  collapseThreshold = DEFAULT_COLLAPSE_THRESHOLD,
  defaultEditing = false,
}: EditableCodeChipSectionProps) => {
  const { theme } = useAppTheme();
  const palette = resolveCodeChipPalette(color);
  const isRefCode = REF_CODE_COLORS.has(color);
  const [isEditing, setIsEditing] = useState(defaultEditing);
  const [isExpanded, setIsExpanded] = useState(false);
  const [input, setInput] = useState("");

  // In edit mode or when expanded, show all chips. Otherwise collapse.
  const shouldCollapse = !isEditing && !isExpanded && collapseThreshold > 0 && codes.length > collapseThreshold;
  const visibleCodes = shouldCollapse ? codes.slice(0, collapseThreshold) : codes;
  const hiddenCount = codes.length - visibleCodes.length;
  /** Edit mode for ref-code colors (ICD/CPT/NDC) routes through the typeahead. */
  const useTypeahead = isEditing && isRefCode;

  const handleAdd = () => {
    const trimmed = input.trim().toUpperCase();
    if (!trimmed) return;
    if (codes.includes(trimmed)) {
      setInput("");
      return;
    }
    onUpdate([...codes, trimmed]);
    setInput("");
  };

  const handleRemove = (code: string) => {
    onUpdate(codes.filter((c) => c !== code));
  };

  return (
    <>
      {/* Section header with Edit/Done toggle */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 2, mb: 0.5 }}>
        <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: "0.08em", color: "text.disabled" }}>
          {label}
        </Typography>
        {canEdit && (
          <Typography
            variant="caption"
            onClick={() => {
              setIsEditing(!isEditing);
              setIsExpanded(false);
              setInput("");
            }}
            sx={{
              cursor: "pointer",
              color: "primary.main",
              fontWeight: 500,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {isEditing ? "Done" : "Edit"}
          </Typography>
        )}
      </Box>

      {/* Chip container */}
      <Box
        sx={{
          bgcolor: "background.paper",
          borderRadius: 1.5,
          border: `1px solid ${theme.palette.divider}`,
          p: 1.5,
          minHeight: 36,
        }}
      >
        {useTypeahead ? (
          <AutocompleteCodeInput
            codeType={color as RefCodeType}
            value={codes}
            onChange={onUpdate}
            placeholder={placeholder}
            maxLength={maxLength}
          />
        ) : (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
            {visibleCodes.map((code) => (
              <Chip
                key={code}
                label={formatToTitleCase(code)}
                size="small"
                onDelete={isEditing ? () => handleRemove(code) : undefined}
                sx={{
                  bgcolor: palette.bg,
                  color: palette.color,
                  fontFamily: "monospace",
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  "& .MuiChip-deleteIcon": { color: palette.color, fontSize: 14 },
                }}
              />
            ))}
            {hiddenCount > 0 && (
              <Typography
                variant="caption"
                onClick={() => setIsExpanded(true)}
                sx={{
                  color: "text.secondary",
                  fontSize: "0.75rem",
                  cursor: "pointer",
                  ml: 0.5,
                  "&:hover": { color: "primary.main" },
                }}
              >
                +{hiddenCount} more
              </Typography>
            )}
            {isExpanded && !isEditing && codes.length > collapseThreshold && (
              <Typography
                variant="caption"
                onClick={() => setIsExpanded(false)}
                sx={{
                  color: "text.disabled",
                  fontSize: "0.75rem",
                  cursor: "pointer",
                  ml: 0.5,
                  "&:hover": { color: "text.secondary" },
                }}
              >
                show less
              </Typography>
            )}
            {isEditing && (
              <TextField
                size="small"
                placeholder={placeholder}
                value={input}
                onChange={(e) => setInput(maxLength ? e.target.value.slice(0, maxLength) : e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAdd();
                  }
                }}
                sx={{
                  "& .MuiInputBase-root": { height: 32, fontSize: "0.75rem", fontFamily: "monospace" },
                  "& .MuiInputBase-input": { py: 0.5, px: 0.75 },
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: palette.color },
                  minWidth: 100,
                  maxWidth: 140,
                }}
              />
            )}
            {codes.length === 0 && !isEditing && (
              <Typography variant="caption" color="text.disabled" sx={{ fontStyle: "italic" }}>
                None
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </>
  );
};
